import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, X, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ShareEarnButton - Floating CTA to drive referral signups
 * Shows on homepage after a short delay, dismissible, remembers user preference
 */
const ShareEarnButton = () => {
  const [isDismissed, setIsDismissed] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem('share_earn_dismissed') === 'true';
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Don't show if dismissed
    if (isDismissed) return;

    // Show after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isDismissed]);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('share_earn_dismissed', 'true');
  };

  if (isDismissed || !isVisible) return null;

  return (
    <div 
      className="fixed bottom-40 right-4 z-40 lg:bottom-24 lg:right-6"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Expanded Card */}
      {isExpanded && (
        <div className="absolute bottom-full right-0 mb-3 w-72 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-[#fb6402] to-[#ff8534] p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Sparkles className="h-5 w-5 mr-2" />
                  <span className="font-bold">Share & Earn</span>
                </div>
                <button 
                  onClick={handleDismiss}
                  className="text-white/80 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">
                Refer friends to Rocket Van Express and earn <span className="font-bold text-[#fb6402]">free deliveries</span>!
              </p>
              <ul className="text-xs text-gray-500 space-y-1 mb-4">
                <li>• 1 referral = 1 free local delivery</li>
                <li>• 3 referrals = R200 mini-move discount</li>
                <li>• 10 referrals = Free mini-move!</li>
              </ul>
              <Link to="/referral">
                <Button className="w-full bg-[#fb6402] hover:bg-[#e55a02] text-sm">
                  Get My Referral Code
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <Link to="/referral">
        <button 
          className="group flex items-center bg-gradient-to-r from-[#fb6402] to-[#ff8534] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
        >
          <div className="p-3">
            <Gift className="h-6 w-6" />
          </div>
          <span className={`font-semibold pr-4 whitespace-nowrap transition-all duration-300 ${
            isExpanded ? 'max-w-40 opacity-100' : 'max-w-0 opacity-0 pr-0'
          }`}>
            Earn Free Deliveries
          </span>
        </button>
      </Link>

      {/* Pulse animation */}
      <div className="absolute -inset-1 bg-[#fb6402] rounded-full animate-ping opacity-20 pointer-events-none"></div>
    </div>
  );
};

export default ShareEarnButton;
